import { LogicalOperators } from "@validereinc/utilities";
import classNames from "classnames/bind";
import { produce } from "immer";
import React, { useMemo, useState } from "react";
import { Button } from "../Button";
import { Card } from "../Card";
import { DropdownMenu } from "../DropdownMenu";
import { DropdownMenuOption } from "../DropdownMenu/types";
import { Icon } from "../Icon/Icon";
import {
  emptyFieldKey,
  generateSampleValueForFilterBuilder,
  getDeepReference,
  getStringKeyFromArrayKey,
  translateAndOr,
} from "./FilterBuilder.helpers";
import styles from "./FilterBuilder.module.scss";
import { FilterBuilderFieldOperatorAndValueInput } from "./Subcomponents/FilterBuilderFieldOperatorAndValueInput";
import { FilterBuilderSubGroupHeaderAndOptions } from "./Subcomponents/FilterBuilderSubGroupHeaderAndOptions";
import { FilterBuilderFilterType, FilterBuilderProps } from "./types";

const cx = classNames.bind(styles);

/**
 * A component that helps users create a flat or nested filter object based on a provided list of fields
 */
export const FilterBuilder = ({
  fields,
  maxDepth = 0,
  filter,
  setFilter,
  title,
  isDisabled,
  style,
  variant = "default",
  className,
  defaultSingleField,
  availableVariables,
  showInputModeToggle = true,
  _recursion = {
    currentJoinOperator: "",
    depth: 0,
    prefixKey: [],
  },
  onFilterChange,
  operatorMappingOverride,
}: FilterBuilderProps) => {
  const [fieldAddOpen, setFieldAddOpen] = useState(false);

  const isRootLevelOfRecursion = _recursion.depth === 0;

  const conditions = useMemo(() => {
    let calculatedConditions: Array<[string, any]> = [];

    if (Array.isArray(filter)) {
      calculatedConditions = filter.map((c) => {
        const k = Object.keys(c);
        if (k.length > 1)
          throw new Error(`No more than 1 condition in $or/$and: ${k.length}`);
        return [k[0], c[k[0]]];
      });
    } else {
      calculatedConditions = Object.entries(filter);
    }

    if (fieldAddOpen) {
      calculatedConditions.push([emptyFieldKey, {}]);
    }

    return calculatedConditions;
  }, [filter, fieldAddOpen]);

  const allowedFieldsToAdd: string[] = useMemo(() => {
    if (isRootLevelOfRecursion) {
      const fieldsAlreadyInRoot = Object.keys(filter).filter(
        (key: string) =>
          key !== LogicalOperators.and && key !== LogicalOperators.or
      );
      return Object.keys(fields).filter(
        (field) => !fieldsAlreadyInRoot.includes(field)
      );
    }
    return Object.keys(fields);
  }, [fields, isRootLevelOfRecursion, filter]);

  // When in root object, only one group can be added:
  const canAddGroups =
    _recursion.depth < maxDepth &&
    !(
      isRootLevelOfRecursion &&
      (LogicalOperators.or in filter || LogicalOperators.and in filter)
    );

  const canAddFields =
    allowedFieldsToAdd.length > 0 && _recursion.depth <= maxDepth;

  const addButtonOptions: DropdownMenuOption[] = useMemo(() => {
    const calculated: DropdownMenuOption[] = [];

    if (canAddFields)
      calculated.push({
        label: "Add to Current Group",
        onClick: () => {
          // If no default single field is set, we can add any field. So display the field picker:
          if (!defaultSingleField) {
            setFieldAddOpen(true);
            return;
          }

          // If a default single field is set, we only should add that single field.
          // So generate a sample value for that field and add it to the filter:
          const newKeyName = defaultSingleField;
          const newFieldDefinition = fields[newKeyName];

          if (!newFieldDefinition) return;

          const sampleValue = generateSampleValueForFilterBuilder({
            data_type: newFieldDefinition.data_type,
            formatTo:
              newFieldDefinition.data_type === "date"
                ? newFieldDefinition.format
                : undefined,
            operatorMappingOverride,
          });

          if (sampleValue === undefined) return;

          const newKeys = _recursion.prefixKey;

          setFilter((oldFilter: FilterBuilderFilterType) => {
            const producedFilter = produce(
              oldFilter,
              (draft: FilterBuilderFilterType) => {
                const targetToAddNewField = getDeepReference(draft, newKeys);
                if (Array.isArray(getDeepReference(oldFilter, newKeys))) {
                  (targetToAddNewField as any[]).push({
                    [newKeyName]: sampleValue,
                  });
                } else {
                  (targetToAddNewField ?? draft)[newKeyName] = sampleValue;
                }
              }
            );

            onFilterChange?.(producedFilter);
            return producedFilter;
          });
        },
      });

    if (canAddGroups)
      calculated.push({
        onClick: () => {
          setFilter((oldFilter: FilterBuilderFilterType) => {
            const producedFilter = produce(
              oldFilter,
              (draft: FilterBuilderFilterType) => {
                const targetToAddNewField = getDeepReference(
                  draft,
                  _recursion.prefixKey
                );
                if (
                  Array.isArray(
                    getDeepReference(oldFilter, _recursion.prefixKey)
                  )
                ) {
                  (targetToAddNewField as any[]).push({
                    [LogicalOperators.and]: [],
                  });
                } else {
                  (targetToAddNewField ?? draft)[LogicalOperators.and] = [];
                }
              }
            );

            onFilterChange?.(producedFilter);
            return producedFilter;
          });
        },
        label: "Create Sub-Group",
      });
    return calculated;
  }, [
    canAddGroups,
    canAddFields,
    filter,
    setFilter,
    JSON.stringify(_recursion.prefixKey),
  ]);

  return (
    <Card
      className={cx("container", {
        [className ?? ""]: !!className && isRootLevelOfRecursion,
      })}
      colorVariant={_recursion.depth > 0 ? "grey" : "stone"}
      style={isRootLevelOfRecursion ? style : undefined}
    >
      {isRootLevelOfRecursion && title && (
        <div className={cx("title")}>{title}</div>
      )}

      {_recursion.currentJoinOperator && (
        <FilterBuilderSubGroupHeaderAndOptions
          _recursion={_recursion}
          setFilter={setFilter}
          onFilterChange={onFilterChange}
          isDisabled={isDisabled}
        />
      )}

      {conditions.map(
        ([fieldKey, fieldValue]: [string, any], conditionIndex) => {
          // Recursively built key:
          const myKey = [
            ..._recursion.prefixKey,
            ...(Array.isArray(filter) ? [conditionIndex] : []),
            fieldKey,
          ];

          return (
            <div
              key={getStringKeyFromArrayKey(myKey)}
              className={cx("condition-line", {
                "is-single-line":
                  !(
                    fieldKey === LogicalOperators.and ||
                    fieldKey === LogicalOperators.or
                  ) && variant !== "vertical",
              })}
            >
              <div
                className={cx("label", "join-label", {
                  "active-label": conditions.length > 1,
                })}
              >
                {conditionIndex > 0 &&
                  translateAndOr(
                    _recursion.currentJoinOperator
                      ? _recursion.currentJoinOperator
                      : LogicalOperators.and
                  )}
              </div>
              {fieldKey === LogicalOperators.and ||
              fieldKey === LogicalOperators.or ? (
                <FilterBuilder
                  _recursion={{
                    prefixKey: myKey,
                    currentJoinOperator: fieldKey,
                    depth: _recursion.depth + 1,
                  }}
                  fields={fields}
                  filter={fieldValue}
                  setFilter={setFilter}
                  onFilterChange={onFilterChange}
                  maxDepth={maxDepth}
                  variant={variant}
                  isDisabled={isDisabled}
                  defaultSingleField={defaultSingleField}
                  availableVariables={availableVariables}
                  showInputModeToggle={showInputModeToggle}
                />
              ) : (
                <FilterBuilderFieldOperatorAndValueInput
                  fieldDefinition={fields[fieldKey]}
                  setFilter={setFilter}
                  onFilterChange={onFilterChange}
                  myKey={myKey}
                  fieldKey={fieldKey}
                  fieldValue={fieldValue}
                  fields={fields}
                  variant={variant}
                  defaultSingleField={defaultSingleField}
                  availableVariables={availableVariables}
                  showInputModeToggle={showInputModeToggle}
                  {...(fieldKey === emptyFieldKey
                    ? {
                        onFieldNameChange: () => {
                          setFieldAddOpen(false);
                        },
                        onRemoveField: () => {
                          setFieldAddOpen(false);
                        },
                        availableFields: allowedFieldsToAdd,
                      }
                    : {})}
                  isDisabled={isDisabled}
                  operatorMappingOverride={operatorMappingOverride}
                />
              )}
            </div>
          );
        }
      )}
      {addButtonOptions.length > 0 && !isDisabled ? (
        <DropdownMenu options={addButtonOptions}>
          <Button
            icon="plus-circle"
            variant="text"
            className={cx("add-filter-button")}
          >
            <div className={cx("add-filter-button-content")}>
              Add Filter
              <Icon
                variant="caret-down"
                weight="fill"
                size={16}
              />
            </div>
          </Button>
        </DropdownMenu>
      ) : null}
    </Card>
  );
};
